import { ServerError } from '@apollo/client'
import type { ErrorResponse } from '@apollo/client/link/error'
import type { TFunction } from 'i18next'

import type { AlertOptions } from '@/modules/shared/providers/AlertProvider'

interface CreateApolloErrorHandlerParams {
  alertDialog: (options: AlertOptions) => void
  genericNotAuthorizedAlert: () => void
  t: TFunction
}

export function createApolloErrorHandler({
  alertDialog,
  genericNotAuthorizedAlert,
  t,
}: CreateApolloErrorHandlerParams) {
  return (error: ErrorResponse) => {
    // eslint-disable-next-line deprecation/deprecation
    const networkError = error.networkError as ServerError
    const operationName = error.operation.operationName
    // eslint-disable-next-line deprecation/deprecation
    const isNotAuthorized = (error?.graphQLErrors || []).some(
      (e) => e?.extensions?.code === 401 && e?.extensions?.status === 'Unauthorized'
    )

    // Any request met with a 401 response
    if (networkError?.statusCode === 401) {
      // Clear persisted cache data on 401
      window.dispatchEvent(new Event('purgeApolloPersistor'))

      // These operations shouldn't raise an alert dialog
      if (
        error.operation.operationName === 'GetSessionUser' ||
        error.operation.operationName === 'GetCurrentPurchaser' ||
        error.operation.operationName === 'SignOut'
      ) {
        return
      }
      return alertDialog({
        type: 'error',
        title: t('alert.unauthenicated.title', 'Please Sign In Again'),
        message: t('alert.unauthenicated.message', 'Your session has expired, please sign in again.'),
        buttonText: t('general.signIn', 'Sign In'),
        onButtonClick() {
          window.location.reload()
        },
      })
    }

    // This happens when we delete a priced catalogued product, but the product has already been deleted.
    if (operationName === 'DeletePricedCataloguedProduct' && networkError?.statusCode === 404) {
      return
    }

    if (networkError?.statusCode >= 500) {
      // this is to help us debug these issues when they appear in Sentry, without triggering more Sentry errors
      console.log(error)

      // These operations shouldn't raise an alert dialog
      if (error.operation.operationName === 'GetDashboardStats') return

      return alertDialog({
        type: 'error',
        title: t('general.internalServerError', 'Internal Server Error'),
        message: t(
          'alert.internalServerError.message',
          'Sorry but there was an internal server error. Please try again later.'
        ),
        buttonText: t('general.okay', 'Okay'),
      })
    }

    if (isNotAuthorized || networkError?.statusCode === 403) {
      return genericNotAuthorizedAlert()
    }

    if (networkError) {
      const operationErrorMessage = networkError?.message
      const statusCode = networkError?.statusCode

      networkError.message = `${operationName} (Status code ${statusCode}): "${operationErrorMessage}"`
    }

    // this is to help us debug these issues when they appear in Sentry, without triggering more Sentry errors
    console.log(error)
    // All other errors
    // `handleErrorLocally` will decide whether to show a global error or handle and show the error locally
    if (!networkError && !error.operation.getContext().handleErrorLocally) alertDialog({ type: 'error' })
  }
}
